import { useState } from "react";
import { Users } from "@/lib/api";
import { useTeam } from "@/lib/team";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, Shuffle, UserCircle } from "@phosphor-icons/react";

export default function Team() {
  const { users, refresh } = useTeam();
  const [open, setOpen] = useState(false);

  const remove = async (id) => {
    if (!confirm("Remove teammate? Their prospects will be unassigned.")) return;
    try {
      await Users.remove(id);
      toast.success("Removed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove teammate");
    }
  };

  const runRoundRobin = async () => {
    if (users.length === 0) return toast.error("Add teammates first");
    try {
      const r = await Users.roundRobin();
      toast.success(`Assigned ${r.total_assigned} prospects across ${r.breakdown.length} teammates`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="team-page">
      <PageHeader
        overline="Workspace · Collaboration"
        title="Team"
        description="Add teammates. Assign prospects manually or round-robin the unassigned queue."
        testId="team-header"
        actions={
          <>
            <Button variant="outline" onClick={runRoundRobin} data-testid="round-robin-btn">
              <Shuffle size={16} className="mr-2" /> Round-robin unassigned
            </Button>
            <Button className="bg-neutral-900 hover:bg-black text-white" onClick={() => setOpen(true)} data-testid="add-teammate-btn">
              <Plus size={16} className="mr-2" /> Add Teammate
            </Button>
          </>
        }
      />

      <div className="p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="team-grid">
        {users.length === 0 && (
          <div className="col-span-full text-center py-20 text-sm text-neutral-500">
            No teammates yet. Add your first person to start assigning prospects.
          </div>
        )}
        {users.map(u => (
          <Card key={u.id} className="p-6 rounded-md border-neutral-200 hover:border-neutral-900 transition-colors" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full grid place-items-center text-white font-mono text-sm" style={{ background: u.color }}>
                  {u.initials}
                </div>
                <div>
                  <div className="font-display text-xl">{u.name}</div>
                  {u.email && <div className="text-xs text-neutral-500 font-mono">{u.email}</div>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`remove-${u.id}`}>
                <Trash size={14} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <AddDialog open={open} onOpenChange={setOpen} onSaved={refresh} />
    </div>
  );
}

function AddDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ name: "", email: "" });
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      await Users.create(f);
      toast.success("Teammate added");
      setF({ name: "", email: "" });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add teammate");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-teammate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add teammate</DialogTitle>
          <DialogDescription>Small teams share the same workspace. Everyone sees everything; prospects can be assigned per person.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Jane Doe" data-testid="input-user-name" /></div>
          <div><Label>Email (optional)</Label><Input value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="jane@company.com" data-testid="input-user-email" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} data-testid="save-user-btn">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
